import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getImageDimensionsByImageUrl } from "../../../../../DesignEditor/views/DesignEditor/utils/smartImageUtils";
import { EditIcon2 } from "../../../../../assets/images/icons/EditIcon2";
import EyeIcon from "../../../../../assets/images/icons/EyeIcon";
import EyeOffIcon from "../../../../../assets/images/icons/EyeOffIcon";
import OpacityIcon from "../../../../../assets/images/icons/OpacityIcon";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaIconButton from "../../../../../components/common/BriaIconButton/BriaIconButton";
import ConfirmationPopup from "../../../../../components/common/ConfirmationPopup/ConfirmationPopup";
import { useLogEvents } from "../../../../../hooks/useLogEvents";
import { useAppStore } from "../../../../../hooks/useStores";
import { ImageEditingConfigComponentProps, ImageEditingViewMode } from "../../../../../models/image-to-image";
import { AutomotiveImageSegment, AutomotiveReflectionLayer } from "../../../../../models/playground";
import styles from "./AutomotiveReflection.module.scss";

type ReflectionProps = {
	setActiveConfig: (config: string) => void;
	isTires?: boolean;
} & ImageEditingConfigComponentProps;

const AutomotiveReflection = ({ setActiveConfig, onBackButtonClick, isTires }: ReflectionProps) => {
	const { playgroundStore } = useAppStore();
	const { projectsStore, automotiveStore } = playgroundStore;
	const { reflectionStore } = automotiveStore;
	const { logReflectionsEvent, logTiresRefinementEvent } = useLogEvents();
	const selectedImage = playgroundStore.singleSelectedImage;
	const selectedReflectionLayer = playgroundStore.selectedReflectionLayer;
	const selectedSegmentation = playgroundStore.selectedSegmentation;
	const [reflectionsList, setReflectionsList] = useState<AutomotiveReflectionLayer[] | undefined>();

	const { t } = useTranslation("translation", {
		keyPrefix: `playground.${playgroundStore.selectedConfig}.config.features.reflection`,
	});

	const { t: tc } = useTranslation("translation", {
		keyPrefix: `playground.${playgroundStore.selectedConfig}.config`,
	});

	useEffect(() => {
		projectsStore.toggleAutomotiveReflectionConfig(true);
		return () => {
			automotiveStore.toggleIsSaved(true);
			playgroundStore.imagesReflections = [];
			projectsStore.toggleAutomotiveReflectionConfig(false);
			playgroundStore.handleSelectSegmentation(null);
		};
	}, [projectsStore, isTires]);

	useEffect(() => {
		if (playgroundStore.imageEditingViewMode === ImageEditingViewMode.GALLERY) {
			onBackButtonClick?.();
		}
	}, [playgroundStore.imageEditingViewMode]);

	useEffect(() => {
		if (selectedImage) {
			const selectedImageReflections = playgroundStore.getAutomotiveImageReflections(selectedImage?.url);
			setReflectionsList(selectedImageReflections?.layers);
		}
	}, [
		playgroundStore.selectedSingleImage,
		playgroundStore.isLoadingReflections,
		playgroundStore.isEditingReflection,
		selectedImage,
		isTires,
	]);

	const toggleSelectedLayer = (layer: AutomotiveReflectionLayer) => {
		playgroundStore.handleSelectSegmentation(null);
		if (selectedReflectionLayer?.name === layer.name) {
			playgroundStore.handleSelectReflectionLayer(null);
		} else {
			playgroundStore.handleSelectReflectionLayer(layer);
		}
	};

	const toggleSelectedSegment = (segment: keyof AutomotiveImageSegment) => {
		const selectedSegments = playgroundStore.imagesSegments.find((item) => item.url === selectedImage?.url);
		const segmentUrl = selectedSegments?.segments?.[segment] ?? "";
		playgroundStore.handleSelectReflectionLayer(null);

		if (selectedSegmentation?.name === segment) {
			playgroundStore.handleSelectSegmentation(null);
		} else {
			playgroundStore.handleSelectSegmentation({
				name: segment,
				url: segmentUrl,
			});
		}
	};

	const handleGenerate = async () => {
		if (isTires) {
			try {
				await playgroundStore.generateTiresLayers();
				logTiresRefinementEvent();
			} catch (error) {
				console.error("Error generating tires layers:", error);
				return;
			}
		} else {
			await playgroundStore.generateReflectionLayers();
			logReflectionsEvent();
		}
		automotiveStore.toggleIsSaved(false);
	};

	const handleSave = async () => {
		automotiveStore.toggleIsSaved(true);
		const reflectionList: AutomotiveReflectionLayer[] | undefined = playgroundStore.getAutomotiveImageReflections(
			selectedImage?.url as string,
		)?.layers;
		reflectionStore.saveReflection(reflectionList, isTires);
	};

	const closeWarningPopup = () => {
		automotiveStore.openWarningPopup = false;
	};

	const switchConfigWithoutSave = () => {
		closeWarningPopup();
		automotiveStore.isSaved = true;
		setActiveConfig(automotiveStore.configToSwitch ?? "");
	};

	const handleConfirmWarning = async () => {
		await handleSave();
		projectsStore.toggleAutomotiveReflectionConfig(false);
		closeWarningPopup();
		setActiveConfig(automotiveStore.configToSwitch ?? "");
	};

	const handleEditLayer = async (layer: AutomotiveReflectionLayer) => {
		if (!selectedImage?.url) return;

		const imageDimensions = await getImageDimensionsByImageUrl(selectedImage?.url);
		const file = await reflectionStore.mergeSegmentationWithDrawing(imageDimensions);
		if (!file) {
			console.error("Failed to generate the merged segmentation file.");
			return;
		}

		isTires
			? await playgroundStore.editTireLayer(file)
			: await playgroundStore.editReflectionLayer(layer.name, file);
		toggleSelectedLayer(layer);
	};

	return (
		<>
			<Box className={styles.reflectionContainer}>
				{!reflectionsList && (
					<Typography className={styles.description}>
						{isTires ? tc("features.tiresRefinement.clickGenerate") : t("clickGenerate")}
					</Typography>
				)}
				<Box className={styles.reflectionsList}>
					{/* Layers */}
					{reflectionsList?.map((layer) => (
						<Box key={layer.name} className={clsx(styles.reflectionItem)}>
							<Box className={styles.itemContent}>
								<Typography className={styles.reflectionTitle}>{t(`layers.${layer.name}`)}</Typography>
								<Box className={styles.reflectionActions}>
									<BriaIconButton
										className={clsx(styles.btn, {
											[styles.disabled]: !layer.url,
										})}
										onClick={() =>
											playgroundStore.showHideReflectionLayer(
												selectedImage?.url ?? "",
												layer.name,
												!layer.hidden,
											)
										}
									>
										{layer.hidden ? <EyeOffIcon /> : <EyeIcon opacity={1} />}
									</BriaIconButton>
									<BriaIconButton
										className={clsx(styles.btn, {
											[styles.selected]: layer.name === selectedSegmentation?.name,
										})}
										onClick={() =>
											toggleSelectedSegment(layer.name as keyof AutomotiveImageSegment)
										}
									>
										<EditIcon2 />
									</BriaIconButton>
									<BriaIconButton
										className={clsx(styles.btn, {
											[styles.selected]: layer.name === selectedReflectionLayer?.name,
											[styles.disabled]: !layer.url,
										})}
										onClick={() => toggleSelectedLayer(layer)}
									>
										<OpacityIcon />
									</BriaIconButton>
								</Box>
							</Box>

							{selectedSegmentation?.name === layer.name && (
								<Box className={styles.editMaskAction}>
									<Typography className={styles.editMaskLabel}>{t("editMaskLabel")}</Typography>
									<BriaButton
										onClick={() => handleEditLayer(layer)}
										loading={playgroundStore.isEditingReflection}
										disabled={playgroundStore.isEditingReflection || !selectedImage}
										buttonType="primaryMedium"
									>
										{t("generate")}
									</BriaButton>
								</Box>
							)}
						</Box>
					))}
				</Box>

				{/* Save/Generate Button */}
				<BriaButton
					buttonType="primaryMedium"
					fullWidth
					className={styles.actionBtn}
					onClick={reflectionsList?.length ? handleSave : handleGenerate}
					disabled={
						playgroundStore.isLoadingReflections ||
						reflectionStore.loadingSaveReflection ||
						playgroundStore.isEditingReflection ||
						!selectedImage
					}
					loading={playgroundStore.isLoadingReflections}
				>
					{t(reflectionsList?.length ? "save" : "generate")}
				</BriaButton>
			</Box>

			<ConfirmationPopup
				open={automotiveStore.openWarningPopup}
				onClose={closeWarningPopup}
				title={tc("warningPopup.title")}
				description={tc("warningPopup.description")}
				confirmButtonText={tc("warningPopup.saveChanges")}
				onClick={handleConfirmWarning}
				onSecondaryClick={switchConfigWithoutSave}
				firstButtonText={tc("warningPopup.undoChanges")}
				loading={playgroundStore.isLoadingReflections}
			/>
		</>
	);
};

export default observer(AutomotiveReflection);
